import * as React from 'react';
import { cn } from '../../lib/utils';

/**
 * The textual equivalent of a chart.
 *
 * A chart drawn as `<svg role="img" aria-label="Line chart">` conveys nothing.
 * A screen-reader user hears the words "line chart" and learns no value, no
 * trend and no exceedance; a printed audit pack carries a picture with no
 * numbers behind it, which is not evidence. WCAG 1.1.1 wants the equivalent,
 * not a name for the picture.
 *
 * `ChartDataTable` renders the underlying values as a real `<table>` that:
 *
 *   - is ALWAYS in the accessibility tree, whether or not it is visible. It is
 *     hidden with `sr-only`, never `display:none` / `hidden`, because those
 *     remove it from the tree — a collapsed alternative that is not readable
 *     is not an alternative.
 *   - can be revealed on screen by anyone (`Show data table`), which is how a
 *     low-vision operator and a sighted auditor both get at the numbers.
 *   - PRINTS. `tokens/accessibility.css` reveals it and hides the chart canvas
 *     under `@media print`, so an exported audit pack contains the values.
 *
 * `summarizeSeries` produces the one-sentence spoken summary that belongs in
 * the chart's own `aria-label`, so the graphic announces what it shows before
 * the reader decides whether to walk the table.
 */

export interface ChartTablePoint {
  /** Category / x-axis label, already formatted for reading. */
  label: string;
  /** Value, or `null` for a genuine gap — never substitute a zero. */
  value: number | null;
}

export interface ChartTableSeries {
  id: string;
  label: string;
  points: ChartTablePoint[];
  /** Unit suffix rendered in the header, e.g. `°C`, `%`, `pcs`. */
  unit?: string;
}

export interface ChartDataTableProps {
  /** What the chart shows. Becomes the table's `<caption>`. */
  caption: string;
  series: readonly ChartTableSeries[];
  /** Header for the category column. Defaults to `Point`. */
  categoryLabel?: string;
  /** Start revealed (audit / print-first surfaces). */
  defaultVisible?: boolean;
  /** Formatter for the numeric cells. */
  formatValue?: (value: number, series: ChartTableSeries) => string;
  className?: string;
}

const defaultFormat = (value: number): string =>
  Number.isInteger(value) ? String(value) : value.toFixed(2);

/**
 * Union of every category label across the series, in first-seen order, so a
 * multi-series chart whose series do not share an x-axis still produces one
 * coherent table rather than one table per series.
 */
function categoryOrder(series: readonly ChartTableSeries[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const s of series) {
    for (const p of s.points) {
      if (!seen.has(p.label)) {
        seen.add(p.label);
        out.push(p.label);
      }
    }
  }
  return out;
}

export function ChartDataTable({
  caption,
  series,
  categoryLabel = 'Point',
  defaultVisible = false,
  formatValue = defaultFormat,
  className,
}: ChartDataTableProps) {
  const [visible, setVisible] = React.useState(defaultVisible);
  const tableId = React.useId();
  const categories = React.useMemo(() => categoryOrder(series), [series]);

  const lookup = React.useMemo(() => {
    const map = new Map<string, Map<string, number | null>>();
    for (const s of series) {
      const inner = new Map<string, number | null>();
      for (const p of s.points) inner.set(p.label, p.value);
      map.set(s.id, inner);
    }
    return map;
  }, [series]);

  if (series.length === 0 || categories.length === 0) return null;

  return (
    <div data-slot="chart-alternative" className={cn('mt-2', className)}>
      <button
        type="button"
        data-slot="chart-data-table-toggle"
        aria-expanded={visible}
        aria-controls={tableId}
        onClick={() => setVisible((v) => !v)}
        className="rounded-md border border-border bg-surface-raised px-2 py-1 text-xs font-medium text-foreground transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {visible ? `Hide data table — ${caption}` : `Show data table — ${caption}`}
      </button>

      {/* `sr-only` rather than `hidden`: the collapsed state must stay in the
        * accessibility tree and in the printed output. */}
      <div id={tableId} data-slot="chart-data-table" className={visible ? 'mt-2' : 'sr-only'}>
        <table className="w-full border-collapse text-left text-xs">
          <caption className="pb-1 text-left text-xs text-muted-foreground">{caption}</caption>
          <thead>
            <tr>
              <th scope="col" className="border-b border-border py-1 pr-3 font-medium">
                {categoryLabel}
              </th>
              {series.map((s) => (
                <th key={s.id} scope="col" className="border-b border-border py-1 pr-3 font-medium">
                  {s.unit ? `${s.label} (${s.unit})` : s.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {categories.map((category) => (
              <tr key={category}>
                <th scope="row" className="border-b border-border/50 py-1 pr-3 font-normal">
                  {category}
                </th>
                {series.map((s) => {
                  const value = lookup.get(s.id)?.get(category);
                  return (
                    <td key={s.id} className="border-b border-border/50 py-1 pr-3 tabular-nums">
                      {value === null || value === undefined
                        ? /* An absent reading is stated, never rendered as 0. */
                          'no data'
                        : formatValue(value, s)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * One-sentence spoken summary of a chart, for its `aria-label`.
 *
 * Reports the shape a reader needs before deciding to walk the table: how many
 * series, over what range, and for each series its low, high, latest and
 * direction. Gaps are counted and stated rather than smoothed over.
 */
export function summarizeSeries(
  kind: string,
  series: readonly ChartTableSeries[],
  format: (value: number) => string = defaultFormat,
): string {
  const first = series[0];
  if (series.length === 0 || !first) return `${kind}, no data`;

  const parts: string[] = [];
  for (const s of series) {
    // `readings` is only the points that carry an actual number. Everything
    // else is a GAP and is counted as one — never coerced to zero. A reader
    // who is told "latest 0" when the sensor simply did not report has been
    // handed a different dataset than the sighted user, which is a worse
    // failure than having no spoken summary at all.
    const readings = s.points.map((p) => p.value).filter((v): v is number => v !== null);
    const gaps = s.points.length - readings.length;
    const gapNote = gaps > 0 ? `, ${gaps} missing reading${gaps === 1 ? '' : 's'}` : '';

    // Plain index access rather than Array.prototype.at, which is ES2022
    // while the workspace targets ES2020. Under `noUncheckedIndexedAccess`
    // both reads are `number | undefined` — exactly the honest type, because
    // an all-gap series has neither endpoint.
    const earliest: number | undefined = readings[0];
    const latest: number | undefined = readings[readings.length - 1];
    if (earliest === undefined || latest === undefined) {
      // Every point in this series is a gap. Say so, rather than inventing a
      // range out of an empty set (Math.min of nothing is Infinity).
      parts.push(`${s.label}: no readings${gaps > 0 ? ` (${gaps} missing)` : ''}`);
      continue;
    }

    const unit = s.unit ? ` ${s.unit}` : '';
    const direction = latest > earliest ? 'rising' : latest < earliest ? 'falling' : 'flat';
    parts.push(
      `${s.label}: low ${format(Math.min(...readings))}${unit}, ` +
        `high ${format(Math.max(...readings))}${unit}, ` +
        `latest ${format(latest)}${unit}, ${direction}${gapNote}`,
    );
  }

  // The spoken x-range comes from the first series' own endpoints. Both may be
  // absent (an empty series), in which case the summary states the series and
  // point counts and omits a range it cannot honestly name.
  const span = first.points;
  const from: string | undefined = span[0]?.label;
  const to: string | undefined = span[span.length - 1]?.label;
  const range = span.length > 1 && from !== undefined && to !== undefined
    ? ` from ${from} to ${to}`
    : '';
  return `${kind}, ${series.length} series, ${span.length} points${range}. ${parts.join('. ')}.`;
}
